import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { apiService } from '@/services/api';
import { useTranslation } from '@/hooks/useTranslation';

interface BoschCode {
  id: string;
  code: string;
  modelName: string;
  brandName: string;
}

const BoshCodesPage: React.FC = () => {
  const { t } = useTranslation();
  const [codes, setCodes] = useState<BoschCode[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    const loadCodes = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await apiService.getBoschCodes({
          page: page + 1,
          limit: rowsPerPage,
          q: searchQuery || undefined,
        });

        setCodes(response.items);
        setTotal(response.total);
      } catch (err: any) {
        console.error('Error loading Bosch codes:', err);
        setError(err.message || t('boshCodes.errors.failedToLoad'));
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(loadCodes, 300);
    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, searchQuery]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  if (loading && codes.length === 0) {
    return (
      <Box
        sx={{
          p: 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('boshCodes.title')}
      </Typography>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder={t('boshCodes.searchPlaceholder')}
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ width: '100%', overflowX: 'auto', minWidth: 0 }}>
            <TableContainer component={Paper} variant="outlined">
              <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography variant="h6">{t('boshCodes.table.code')}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6">{t('boshCodes.table.model')}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6">{t('boshCodes.table.brand')}</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {codes.map(code => (
                  <TableRow key={code.id}>
                    <TableCell>{code.code}</TableCell>
                    <TableCell>{code.modelName}</TableCell>
                    <TableCell>{code.brandName}</TableCell>
                  </TableRow>
                ))}
                {codes.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography variant="body2" color="text.secondary">
                        {t('boshCodes.noCodesFound')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default BoshCodesPage;


